import { useState, useEffect, useCallback }  from 'react';
import { useQuery }                           from '@tanstack/react-query';
import {
  IndianRupee, Stethoscope, BedDouble, ShieldCheck,
  ClipboardList, Banknote, XCircle, Microscope,
  Timer, BarChart2, UserCheck, Pill, Scissors,
  RotateCcw, Users,
  type LucideProps,
} from 'lucide-react';
import { api }                                from '@/lib/api';
import { useAuthStore }                       from '@/store/auth.store';

/** Maps the `icon` key from the API report registry to a Lucide component. */
const REPORT_ICON_MAP: Record<string, React.ComponentType<LucideProps>> = {
  IndianRupee, Stethoscope, BedDouble, ShieldCheck,
  ClipboardList, Banknote, XCircle, Microscope,
  Timer, BarChart2, UserCheck, Pill, Scissors,
  RotateCcw, Users,
};

// ── Types ────────────────────────────────────────────────────────────────────
interface ColDef {
  key:    string;
  header: string;
  width:  number;
  fmt?:   'currency' | 'date' | 'percent' | 'integer' | 'number';
}

interface ReportMeta {
  type:     string;
  title:    string;
  subtitle: string;
  category: 'admin' | 'billing' | 'diagnostics' | 'clinical';
  icon:     string;
  cols:     ColDef[];
}

interface PreviewResponse {
  reportType: string;
  title:      string;
  cols:       ColDef[];
  total:      number;
  rows:       Record<string, unknown>[];
  truncated:  boolean;
}

// ── Date range presets ───────────────────────────────────────────────────────
type Preset = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'custom';

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function presetDates(p: Preset): { start: string; end: string } {
  const now   = new Date();
  const today = isoDate(now);

  if (p === 'today')     return { start: today, end: today };

  if (p === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const d = isoDate(y);
    return { start: d, end: d };
  }

  if (p === 'this_week') {
    const mon = new Date(now);
    mon.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
    return { start: isoDate(mon), end: today };
  }

  if (p === 'this_month') {
    return { start: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
  }

  if (p === 'last_month') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: isoDate(first), end: isoDate(last) };
  }

  return { start: today, end: today };
}

// ── Cell formatter ────────────────────────────────────────────────────────────
function fmtCell(value: unknown, fmt?: ColDef['fmt']): string {
  if (value === null || value === undefined) return '—';
  switch (fmt) {
    case 'currency': {
      const n = Number(value);
      return isNaN(n) ? String(value) : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    }
    case 'date': {
      const d = new Date(String(value));
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'integer':
      return Number(value).toLocaleString('en-IN');
    case 'number':
      return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    default:
      return String(value);
  }
}

// ── Category labels ───────────────────────────────────────────────────────────
const CAT_LABELS: Record<string, string> = {
  admin:       'Hospital Admin',
  billing:     'Billing & Finance',
  diagnostics: 'Diagnostics (Lab / Radiology)',
  clinical:    'Clinical / Doctor',
};

// ── Download helper — sends bearer token via fetch, saves blob ───────────────
async function downloadReport(
  reportType: string,
  format:     'excel' | 'pdf',
  start:      string,
  end:        string,
  token:      string,
  title:      string,
) {
  const url = `/api/v1/reports/${reportType}/export?start=${start}&end=${end}&format=${format}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((body.error as string) ?? `Export failed (${res.status})`);
  }

  const blob  = await res.blob();
  const href  = URL.createObjectURL(blob);
  const ext   = format === 'excel' ? 'xlsx' : 'pdf';
  const a     = document.createElement('a');
  a.href      = href;
  a.download  = `${title.replace(/[^a-z0-9]/gi, '_')}_${start}_${end}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

// ── Preview Table ─────────────────────────────────────────────────────────────
function PreviewTable({ data }: { data: PreviewResponse }) {
  const numericFmts = new Set<ColDef['fmt']>(['currency', 'number', 'integer', 'percent']);

  return (
    <div className="overflow-auto rounded-lg border border-slate-200">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-blue-800 text-white">
            {data.cols.map(c => (
              <th
                key={c.key}
                className="px-3 py-2 text-left font-semibold whitespace-nowrap tracking-wide"
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr>
              <td colSpan={data.cols.length} className="text-center py-10 text-slate-400">
                No data for the selected date range.
              </td>
            </tr>
          )}
          {data.rows.map((row, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-slate-50 hover:bg-blue-50'}
            >
              {data.cols.map(c => (
                <td
                  key={c.key}
                  className={`px-3 py-1.5 whitespace-nowrap border-b border-slate-100 ${
                    numericFmts.has(c.fmt) ? 'text-right tabular-nums' : ''
                  }`}
                >
                  {fmtCell(row[c.key], c.fmt)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function ReportsDashboard() {
  const token = useAuthStore(s => s.accessToken) ?? '';

  // Available reports (RBAC-filtered by server)
  const { data: reports = [], isLoading: reportsLoading } = useQuery<ReportMeta[]>({
    queryKey: ['reports-list'],
    queryFn:  () => api.get('/reports').then((r: { data: ReportMeta[] }) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const [selectedType, setSelectedType]       = useState<string>('');
  const [preset, setPreset]                   = useState<Preset>('this_month');
  const [customStart, setCustomStart]         = useState(isoDate(new Date()));
  const [customEnd, setCustomEnd]             = useState(isoDate(new Date()));
  const [downloading, setDownloading]         = useState<'excel' | 'pdf' | null>(null);
  const [downloadError, setDownloadError]     = useState('');

  // Auto-select first report once list loads
  useEffect(() => {
    if (reports.length > 0 && !selectedType) setSelectedType(reports[0].type);
  }, [reports, selectedType]);

  const { start, end } = preset !== 'custom'
    ? presetDates(preset)
    : { start: customStart, end: customEnd };

  const selectedMeta = reports.find(r => r.type === selectedType);

  // Preview query — refetches on report/date change
  const { data: preview, isLoading: previewLoading, isError, error } = useQuery<PreviewResponse>({
    queryKey: ['report-preview', selectedType, start, end],
    queryFn:  () => api.get(`/reports/${selectedType}?start=${start}&end=${end}&limit=50`).then((r: { data: PreviewResponse }) => r.data),
    enabled:  !!selectedType && !!start && !!end,
    staleTime: 30_000,
  });

  const handleDownload = useCallback(async (fmt: 'excel' | 'pdf') => {
    if (!selectedMeta) return;
    setDownloading(fmt);
    setDownloadError('');
    try {
      await downloadReport(selectedType, fmt, start, end, token, selectedMeta.title);
    } catch (e: any) {
      setDownloadError(e.message ?? 'Download failed');
    } finally {
      setDownloading(null);
    }
  }, [selectedType, start, end, token, selectedMeta]);

  // Group reports by category for sidebar
  const grouped = reports.reduce<Record<string, ReportMeta[]>>((acc, r) => {
    if (!acc[r.category]) acc[r.category] = [];
    acc[r.category].push(r);
    return acc;
  }, {});

  const PRESET_LABELS: Record<Preset, string> = {
    today:      'Today',
    yesterday:  'Yesterday',
    this_week:  'This Week',
    this_month: 'This Month',
    last_month: 'Last Month',
    custom:     'Custom',
  };

  return (
    <div className="flex h-full min-h-screen bg-slate-50">

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">Reports</h2>
          <p className="text-xs text-slate-500 mt-0.5">Enterprise Reporting Engine</p>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {reportsLoading && (
            <p className="px-5 py-4 text-xs text-slate-400 animate-pulse">Loading reports…</p>
          )}

          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mb-4">
              <p className="px-5 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                {CAT_LABELS[cat] ?? cat}
              </p>
              {items.map(r => (
                <button
                  key={r.type}
                  onClick={() => setSelectedType(r.type)}
                  className={`w-full flex items-start gap-2 px-4 py-2.5 text-left transition-colors ${
                    selectedType === r.type
                      ? 'bg-blue-50 border-r-2 border-blue-700 text-blue-900'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {(() => { const I = REPORT_ICON_MAP[r.icon]; return I ? <I size={14} className="shrink-0 mt-0.5 text-slate-500"/> : null; })()}
                  <div>
                    <p className="text-xs font-medium leading-snug">{r.title}</p>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* Report header + filter bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          {selectedMeta ? (
            <>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    {(() => { const I = REPORT_ICON_MAP[selectedMeta.icon]; return I ? <I size={18} className="text-navy shrink-0"/> : null; })()}
                    {selectedMeta.title}
                  </h1>
                  <p className="text-sm text-slate-500 mt-0.5">{selectedMeta.subtitle}</p>
                </div>

                {/* Download buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleDownload('excel')}
                    disabled={!!downloading || previewLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium
                               hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloading === 'excel' ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    ) : '⬇'}
                    Download Excel
                  </button>

                  <button
                    onClick={() => handleDownload('pdf')}
                    disabled={!!downloading || previewLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium
                               hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloading === 'pdf' ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    ) : '⬇'}
                    Download PDF
                  </button>
                </div>
              </div>

              {downloadError && (
                <p className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-md">{downloadError}</p>
              )}

              {/* Date range bar */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {(Object.keys(PRESET_LABELS) as Preset[]).map(p => (
                  <button
                    key={p}
                    onClick={() => setPreset(p)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      preset === p
                        ? 'bg-blue-700 text-white border-blue-700'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-700'
                    }`}
                  >
                    {PRESET_LABELS[p]}
                  </button>
                ))}

                {preset === 'custom' && (
                  <div className="flex items-center gap-2 ml-2">
                    <input
                      type="date"
                      value={customStart}
                      onChange={e => setCustomStart(e.target.value)}
                      className="border border-slate-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-slate-400 text-xs">to</span>
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart}
                      onChange={e => setCustomEnd(e.target.value)}
                      className="border border-slate-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {start && end && (
                  <span className="ml-auto text-xs text-slate-400">
                    {start === end ? start : `${start} → ${end}`}
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400">Select a report from the sidebar.</p>
          )}
        </div>

        {/* Preview table area */}
        <div className="flex-1 overflow-auto p-6">
          {previewLoading && (
            <div className="flex items-center justify-center h-40 text-slate-400">
              <svg className="animate-spin h-6 w-6 mr-2" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Loading preview…
            </div>
          )}

          {isError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
              {(error as any)?.response?.data?.error ?? 'Failed to load report preview.'}
            </div>
          )}

          {preview && !previewLoading && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">
                  Showing first <span className="font-semibold text-slate-700">{preview.rows.length}</span> of{' '}
                  <span className="font-semibold text-slate-700">{preview.total}</span> records
                  {preview.truncated && (
                    <span className="ml-1 text-amber-600 font-medium">
                      (download for complete data)
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-400">{selectedMeta?.subtitle}</p>
              </div>
              <PreviewTable data={preview} />
            </>
          )}

          {!selectedType && !reportsLoading && (
            <div className="flex flex-col items-center justify-center h-64 text-slate-300">
              <svg className="h-14 w-14 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0120 9.414V19a2 2 0 01-2 2z"/>
              </svg>
              <p className="text-sm">Select a report from the left panel to begin</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
